import { useParlorRequestB } from "@/components/ParlorRequestExperiment";
import { BaseButton } from "goi_common/src/design-system";
import { allowIntentionalLeave } from "@/containers/Parlor/ParlorRequestContainer/hooks/useParlorRouterChangeLoad";
import { HEADER_HEIGHT, SECOND_HEADER_HEIGHT } from "@/constants/components";
import { markMatchRequestOnboardingEligible } from "@/utils/matchRequestOnboardingExperiment";
import { hasAdEntryParams } from "@/utils/parlorRequestAdEntry";
import { isSangjoLandingAdTarget } from "@/utils/sangjoLandingExperiment";
import { useActivePathCheck } from "@/hooks/useActivePathCheck";
import { useIsFromPrime } from "@/hooks/useIsFromPrime";

import {
  colors,
  HStack,
  TabBar,
  GoiLogo,
  Spacing,
  GLOBAL_MAX_WIDTH,
  NextLink,
  customEvent,
  EventTriggerAndTargetType,
  Hamburger,
  useResponsive,
  VStack,
  responsive,
  call,
  getPrimeUrl,
  COST_CALC_HEADER_URL,
  COST_CARD_ARM_EVENT,
  shouldHeaderGoToCostCalc,
} from "goi_common";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import Link from "next/link";
import { useServicePackage } from "@/containers/ServicePackageContainer/hooks/useServicePackage";
import reviewData from "goi_common/src/constants/reviewCount.json";
import HeaderButton from "./HeaderButton";
import { safeParlorIds } from "@/constants/parlor/excludeParlors";
import { useIsFacilityRequestRollback } from "@/components/FacilityRequestRollbackExperiment";
import { useFacilityDetailCostHubArm } from "@/components/FacilityDetailCostHubExperiment";
import { useIsReviewRenewalB } from "@/components/ReviewRenewalExperiment";
import { useHideCallCta } from "@/hooks/useHideCallCta";
import { useCrematoriumInquiryModal } from "@/hooks/useCrematoriumInquiryModal";
import { useParlorInquiryModal } from "@/hooks/useParlorInquiryModal";

/**
 * GNB 24시간 고객센터 버튼이 문의 모달을 띄워야 하는 화장장 화면과, 그때 쓸 이벤트 path.
 * 여기 없는 경로에서는 기존대로 바로 발신한다.
 */
const CREMATORIUM_INQUIRY_EVENT_PATH: Record<
  string,
  "crematorium_detail" | "crematorium_recommendation" | undefined
> = {
  "/crematorium/[id]": "crematorium_detail",
  "/crematorium-recommendation/[id]": "crematorium_recommendation",
  "/crematorium-recommendation/[id]/place/[keywordId]": "crematorium_recommendation",
};

/**
 * GNB 24시간 고객센터 버튼이 장례식장 문의 모달을 띄워야 하는 화면과 그때 쓸 이벤트 path.
 * 특정 장례식장이 정해지는 화면만 넣는다. 지역 목록은 조문 문의가 걸 번호를 정할 수 없다.
 */
const PARLOR_INQUIRY_EVENT_PATH: Record<
  string,
  "parlor_detail" | "parlor_estimates" | "parlor_match_request" | undefined
> = {
  "/parlor/[id]": "parlor_detail",
  "/parlor/[id]/family/[keywordId]": "parlor_detail",
  "/parlor/[id]/estimates": "parlor_estimates",
  "/parlor/[id]/prices/[type]": "parlor_estimates",
  "/match/parlor-requests/[id]": "parlor_match_request",
};

export function Header({ onOpen }: { onOpen: () => void }) {
  const isParlorB = useParlorRequestB();
  const router = useRouter();

  const handleHamburgerClick = () => {
    customEvent({
      newGtm: {
        path: "gnb",
        trigger_and_target: "hamburger",
      },
    });
    onOpen();
  };
  const isServicePage = useMemo(() => {
    return (
      router.pathname === "/service" ||
      router.pathname.includes("/service/[type]") ||
      router.pathname.includes("/premium/service/[type]/")
    );
  }, [router]);

  const isService2025Page = useMemo(() => {
    return ["/service/2025", "/service/2025/[type]"].includes(router.pathname);
  }, [router]);

  const isProductPage = useMemo(() => {
    if (router.pathname.includes("/product/consult/exclusive")) return false;
    if (router.pathname.includes("/product/consult/proposal")) return false;
    return (
      router.pathname.includes("/product") || router.pathname.includes("/company-introduction")
    );
  }, [router]);

  // parlor-requests는 비용 게이지 집중 노출을 위해 헤더 밑 sub-nav(메뉴)를 항상 숨김.
  // 실험 #296 C안(sub-nav 접기 + 비용 게이지 sticky)을 디폴트로 승격. Safe/NotSafe 공통 적용.
  const isParlorRequestsRoute = router.pathname === "/match/parlor-requests/[id]";
  const isParlorRequestsCompactHeader = isParlorRequestsRoute;

  // #306 C 채택(실험 종료): 광고 다이렉트 /match/request는 항상 하단 메뉴바(GNB sub-nav) 접기.
  // (로고 헤더는 유지. 오가닉은 게이트 밖이라 현행 유지.)
  // 단, 상조 랜딩(?template=sangjo)과 실험 #368 모수(A·B 공통)는 광고 유입이어도 sub-nav를 유지한다(기획 요청).
  const isMatchReqAdDirect =
    router.pathname === "/match/request" &&
    hasAdEntryParams(router.query) &&
    router.query.template !== "sangjo" &&
    !isSangjoLandingAdTarget(router.query);
  const isMatchReqAdCompactHeader = isMatchReqAdDirect;

  // 개별 장지 견적(facility-requests/[id])은 sticky 비용 게이지에 집중시키기 위해 sub-nav를 숨긴다.
  // parlor-requests와 동일 처리 (#296 C안 패턴).
  //
  // 실험 #338 B(롤백)에 걸린 사용자만 sub-nav를 되돌려 받는다. 값은 여기서 훅을 부르지 않고
  // FacilityRequestRollbackProvider 한 곳에서 내려온다. Header는 전 페이지에 뜨기 때문에
  // 여기서 useCustomVariation을 부르면 모수가 사이트 전체로 번진다(#296에서 실제로 그랬다).
  const isFacilityRequestRollback = useIsFacilityRequestRollback();
  const isFacilityRequestsCompactHeader =
    router.pathname === "/match/facility-requests/[id]" && !isFacilityRequestRollback;

  // 여유+재제출 결과 페이지도 parlor-requests/facility-requests와 같은 이유로 sub-nav를 숨긴다.
  // 모바일 전용 처리라 PC 폭에서는 sub-nav가 그대로 보인다(기존 3개 라우트와 동일 컨벤션).
  const isRelaxedResultCompactHeader = router.pathname === "/match/recommendation/relaxed";

  const isMobileHeaderTabBarHidden = useMemo(() => {
    if (router.pathname === "/parlor/[id]") return true;
    if (isParlorRequestsCompactHeader) return true;
    if (isMatchReqAdCompactHeader) return true;
    if (isFacilityRequestsCompactHeader) return true;
    if (isRelaxedResultCompactHeader) return true;
    return false;
  }, [
    router,
    isParlorRequestsCompactHeader,
    isMatchReqAdCompactHeader,
    isFacilityRequestsCompactHeader,
    isRelaxedResultCompactHeader,
  ]);

  const isSafeParlor = useMemo(() => {
    return router.pathname === "/parlor/[id]" && safeParlorIds.includes(Number(router.query.id));
  }, [router]);

  const { isBiggerThanLg } = useResponsive();
  const isFromPrime = useIsFromPrime();

  return (
    <header>
      <div
        css={responsive({
          position: "fixed",
          width: "100%",
          height: `${HEADER_HEIGHT}px`,
          top: 0,
          zIndex: 1000,
          backgroundColor: colors.white,
          borderBottom: {
            base: "none",
            lg: `1px solid ${colors.gray100}`,
          },
          transition: "none",
          userSelect: "none",
        })}
      >
        <VStack
          margin="0 auto"
          css={responsive({
            width: {
              base: "100%",
              lg: `${GLOBAL_MAX_WIDTH}px`,
            },
          })}
        >
          <HStack height={HEADER_HEIGHT}>
            <HStack
              padding="13px 16px 13px 20px"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              {isFromPrime ? (
                <a
                  id="goi-logo"
                  href={getPrimeUrl("/")}
                  css={{ outline: "none", textDecoration: "none" }}
                  onClick={() => {
                    customEvent({
                      newGtm: { path: "gnb", trigger_and_target: "logo-uv_home" },
                    });
                  }}
                >
                  <GoiLogo color={colors.goiBlack} size={30} />
                </a>
              ) : (
                <NextLink
                  id="goi-logo"
                  href="/"
                  onClick={() => {
                    customEvent({
                      newGtm: { path: "gnb", trigger_and_target: "logo-uv_home" },
                    });
                  }}
                >
                  <GoiLogo color={colors.goiBlack} size={30} />
                </NextLink>
              )}
              <HStack
                alignItems="center"
                style={{
                  background: "white",
                }}
              >
                <div
                  css={responsive({
                    display: {
                      lg: "block",
                      base: "none",
                    },
                  })}
                >
                  <HeaderTabBar isFromPrime={isFromPrime} />
                </div>

                <Spacing orientation="horizontal" gap={isParlorB ? 0 : 36} />
                {isParlorB ? (
                  <Box
                    sx={{
                      button: {
                        padding: "6px 12px",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#46494C",
                      },
                    }}
                  >
                    <BaseButton
                      type="assistive_2"
                      size="small"
                      onClick={() => {
                        customEvent({
                          newGtm: {
                            path: "parlor_match_request",
                            trigger_and_target: "hdr_mubinso",
                          },
                        });
                        allowIntentionalLeave();
                        void router.push("/match/request/?template=mubinso");
                      }}
                    >
                      무빈소 알아보기
                    </BaseButton>
                  </Box>
                ) : (
                  <HeaderButtons isHomeViewportTop={false} homePath="/" />
                )}

                <Spacing orientation="horizontal" gap={8} />
                <NextLink
                  href={isParlorB ? "/funeral-facilities/parlor/" : "/funeral-facilities/"}
                  onClick={() => {
                    if (isParlorB) {
                      customEvent({
                        newGtm: { path: "parlor_match_request", trigger_and_target: "hdr_compare" },
                      });
                      allowIntentionalLeave();
                    }
                    customEvent({
                      newGtm: { path: "gnb", trigger_and_target: "nav-uv_facility" },
                    });
                  }}
                >
                  <HStack
                    alignItems="center"
                    justifyContent="center"
                    gap="2px"
                    css={{
                      background: "#F4F5F6",
                      borderRadius: "6px",
                      padding: "6px 12px",
                    }}
                  >
                    <img
                      src="/platform/home/facility_find_16.svg"
                      width={16}
                      height={16}
                      alt=""
                      css={{ display: "block" }}
                    />
                    <span className="body_14_m" css={{ color: "#46494C" }}>
                      {isParlorB ? "장례식장 비교" : "시설 찾기"}
                    </span>
                  </HStack>
                </NextLink>

                <Spacing orientation="horizontal" gap={isParlorB ? 8 : 16} />
                <Box position="relative">
                  <button
                    onClick={handleHamburgerClick}
                    css={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <Hamburger color={colors.gray900} size={24} />
                  </button>
                </Box>
              </HStack>
            </HStack>
          </HStack>

          {(isFromPrime || !isMobileHeaderTabBarHidden) && (
            <div
              css={responsive({
                background: "white",
                display: {
                  lg: "none",
                  base: "block",
                },
                borderBottom: {
                  lg: "none",
                  base: `1px solid ${colors.gray100}`,
                },
              })}
            >
              <HeaderTabBar isFromPrime={isFromPrime} />
            </div>
          )}
        </VStack>
      </div>
      <Spacing
        gap={
          isBiggerThanLg
            ? HEADER_HEIGHT
            : isSafeParlor ||
              isParlorRequestsCompactHeader ||
              isMatchReqAdCompactHeader ||
              isFacilityRequestsCompactHeader ||
              isRelaxedResultCompactHeader
            ? HEADER_HEIGHT
            : HEADER_HEIGHT + SECOND_HEADER_HEIGHT
        }
      />

      {(isServicePage || isService2025Page) && (
        <HeaderServiceTabBar year={isService2025Page ? 2025 : 2026} />
      )}

      {isProductPage && <HeaderProductTabBar />}
    </header>
  );
}

export const MENU_DATA = {
  견적: {
    to: "/match/request/",
    activePath: "견적",
  },
  상품안내: {
    to: "/product",
    activePath: "상품",
  },
  가이드북: {
    to: "/guidebook/",
    activePath: "가이드북",
  },
  후기: {
    to: "/review/",
    activePath: "후기",
  },
} as const;

const MENUS: {
  text: string;
  to: string;
  eventDetails: EventTriggerAndTargetType["gnb"][];
  activePath: string;
  /** 탭 오른쪽 주황 배지. 상품안내의 N 과 같은 모양을 쓴다. */
  badge?: string;
}[] = [
  // {
  //   text: "홈",
  //   to: "/",
  //   eventDetails: ["nav-uv_home"],
  // },
  {
    text: "장례견적",
    to: MENU_DATA["견적"].to,
    eventDetails: ["nav-uv_match_request"],
    activePath: MENU_DATA["견적"].activePath,
  },
  {
    text: "상품안내",
    to: "/product",
    eventDetails: ["nav-uv_prime"],
    activePath: "상품",
  },
  {
    text: "장례절차",
    to: MENU_DATA["가이드북"].to,
    eventDetails: ["nav-uv_guidebook"],
    activePath: MENU_DATA["가이드북"].activePath,
  },
  {
    text: "고객후기",
    to: MENU_DATA["후기"].to,
    eventDetails: ["review-uv_review"],
    activePath: MENU_DATA["후기"].activePath,
  },

  // {
  //   text: "이벤트",
  //   to: "https://prime.goifuneral.co.kr/promotion?utm_campaign=platform",
  //   eventDetails: ["nav-uv_prime_event"],
  // },
  // {
  //   text: "24시간 전화",
  //   to: "tel:1666-9784",
  //   eventDetails: ["call-opn_call_mdl"],
  // },
];

/**
 * 장지 상세(/facilities/[id]) 전용 탭바 메뉴.
 *
 * 전역 4메뉴(장례견적/상품안내/장례절차/고객후기) 대신 장례 니즈로 넘어가는 3개만 남긴다.
 * 장지를 보러 온 사람에게 상품안내·고객후기는 다음 행동이 아니라는 판단.
 *
 * activePath 는 이 페이지에서 useActivePathCheck 가 "" 를 돌려주므로, 빈 문자열을 쓰면
 * 3개가 전부 활성으로 켜진다. 절대 매칭되지 않는 값을 넣어 활성 표시를 끈다.
 *
 * "장례 견적" 은 CTA 02(비용 허브 카드 우측)와 같은 목적지다. ?template=cost 는
 * MatchRequestContainer 에서 #316 온보딩 분기보다 우선 처리되므로
 * markMatchRequestOnboardingEligible 마킹은 걸지 않는다.
 */
const FACILITY_DETAIL_MENUS: typeof MENUS = [
  {
    text: "장례 견적",
    to: "/match/request/?template=cost",
    eventDetails: ["fac_nav-uv_match_cost"],
    activePath: "fac_nav_cost",
    // 이 페이지에서는 활성 메뉴가 없어 3개가 모두 회색이라 눈에 안 띈다.
    // 첫 칸에 배지를 달아 시선을 잡으면 나머지 둘도 같이 읽힌다.
    badge: "N",
  },
  {
    text: "장지 추천받기",
    to: "/match/facility-requests/",
    eventDetails: ["fac_nav-uv_facility_req"],
    activePath: "fac_nav_facility",
  },
  {
    text: "빈소없는 장례",
    to: "/match/request/?template=mubinso",
    eventDetails: ["fac_nav-uv_mubinso"],
    activePath: "fac_nav_mubinso",
  },
];

function HeaderTabBar({ isFromPrime }: { isFromPrime: boolean }) {
  const { activePath } = useActivePathCheck();
  const { isBiggerThanLg } = useResponsive();
  const router = useRouter();
  const reviewCount = reviewData.count;
  const costHubArm = useFacilityDetailCostHubArm();
  const isReviewRenewalB = useIsReviewRenewalB();
  /** 카드 실험(#361~#363) B 로 기록된 사람은 "장례견적"이 계산기로 간다. 배정은 하지 않고 기록만 읽는다 */
  const [quoteToCalc, setQuoteToCalc] = useState(false);
  useEffect(() => {
    // 처음 배정되는 페이지에서는 카드 게이트가 헤더보다 늦게 기록하므로 알림을 받아 다시 읽는다
    const sync = () => setQuoteToCalc(shouldHeaderGoToCostCalc());
    sync();
    window.addEventListener(COST_CARD_ARM_EVENT, sync);
    return () => window.removeEventListener(COST_CARD_ARM_EVENT, sync);
  }, [router.asPath]);
  /** #350 할당 전에는 팔마다 다른 메뉴를 그리지 않는다. 자리는 잡고 내용만 비운다. */
  const isCostHubUnassigned = costHubArm === null;

  const { isHide } = useHandleHeaderServiceTabBar({
    firstOffset: 0,
  });

  const isHeaderTabBarHidden = useMemo(() => {
    if (isFromPrime) return false;
    const conditionedPath =
      router.asPath.includes("service") ||
      router.asPath.includes("product") ||
      router.asPath.startsWith("/message/") ||
      // 후기 목록 B안 (시안 주석: 아래로 스크롤 시 탭 바 숨김, 필터는 표시, 위로 스크롤 시 전체 표시). A안(현행)은 그대로 둔다
      (router.pathname === "/review" && isReviewRenewalB);
    return isBiggerThanLg ? false : conditionedPath ? isHide : false;
  }, [isHide, isBiggerThanLg, router.asPath, router.pathname, isFromPrime, isReviewRenewalB]);

  /**
   * #350 B안에서만 장지 상세 전용 3메뉴로 교체한다.
   * 다른 경로는 Provider 바깥이라 항상 "A"이며, 헤더 클릭만으로는 실험에 할당되지 않는다.
   * 메뉴 선택과 정렬이 같은 조건을 봐야 해서 플래그를 하나만 둔다.
   */
  const isFacilityDetailMenus =
    !isFromPrime && router.pathname === "/facilities/[id]" && costHubArm === "B";

  const menus = useMemo(() => {
    if (isFacilityDetailMenus) {
      return FACILITY_DETAIL_MENUS;
    }
    if (!isFromPrime) return MENUS;
    return [
      {
        text: "읽을거리",
        to: getPrimeUrl("/content"),
        eventDetails: ["nav-uv_guidebook"] as EventTriggerAndTargetType["gnb"][],
        activePath: "content",
      },
      {
        text: "고객후기",
        to: "/review/",
        eventDetails: ["review-uv_review"] as EventTriggerAndTargetType["gnb"][],
        activePath: "후기",
      },
      {
        text: "100원상조",
        to: getPrimeUrl("/"),
        eventDetails: ["nav-uv_prime"] as EventTriggerAndTargetType["gnb"][],
        activePath: "",
      },
      {
        text: "장례견적",
        to: "/match/request/",
        eventDetails: ["nav-uv_match_request"] as EventTriggerAndTargetType["gnb"][],
        activePath: "견적",
      },
    ];
  }, [isFacilityDetailMenus, isFromPrime]);

  return isHeaderTabBarHidden ? null : (
    <Box
      margin="0 10px"
      css={{
        overflow: "auto",
        // #350 할당 전 자리 유지. display:none 이면 탭바 높이가 무너져 헤더가 흔들린다.
        visibility: isCostHubUnassigned ? "hidden" : "visible",
      }}
    >
      <TabBar
        // 장지 상세 3메뉴는 개수가 적다. space-between 은 양끝으로 벌어져 읽히지 않고,
        // center 는 가운데로 몰린다. space-around 로 그 중간에 둔다.
        gap={isBiggerThanLg ? 32 : 0}
        justifyContent={isFacilityDetailMenus ? "space-around" : "space-between"}
      >
        {menus.map((item, i) => {
          const isExternalLink = item.to.startsWith("http");
          const isActive = isExternalLink ? false : activePath === item.activePath;
          const isReview = item.to === "/review/";
          const isPrime = item.to.includes("product");

          const isQuoteToCalc = quoteToCalc && item.eventDetails.includes("nav-uv_match_request");
          const to = isQuoteToCalc ? COST_CALC_HEADER_URL : item.to;

          const handleTabClick = () => {
            // 실험 #316: 헤더 "장례견적" 클릭 진입을 온보딩 A/B 자격으로 마킹.
            if (item.eventDetails.includes("nav-uv_match_request") && !isQuoteToCalc) {
              markMatchRequestOnboardingEligible();
            }
            item.eventDetails.map((trigger_and_target) => {
              customEvent({ newGtm: { path: "gnb", trigger_and_target } });
            });
          };

          const tabContent = (
            <TabBar.Tab
              className={`${isActive ? "subtitle_16_b gray900" : "body_16_m gray500"} gray500`}
              css={responsive({
                padding: "6px 0px",
                margin: "6px",
                whiteSpace: "nowrap",
                textAlign: "center",
                color: { base: isActive ? colors.gray900 : "" },
              })}
              onClick={handleTabClick}
            >
              <HStack gap="2px" alignItems="center" justifyContent="center">
                {/* {isPrime && (
                      <Box ... >EVENT</Box>
                    )} */}
                {item.text}
                {item.badge && (
                  <Box
                    className="white"
                    padding="1px 6px"
                    backgroundColor={colors.orange700}
                    borderRadius="20px"
                    css={{
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: "500",
                      lineHeight: "18px",
                    }}
                  >
                    {item.badge}
                  </Box>
                )}
                {isPrime && (
                  <Box
                    className="white"
                    padding="1px 6px"
                    backgroundColor={colors.orange700}
                    borderRadius="20px"
                    css={{
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: "500",
                      lineHeight: "18px",
                    }}
                  >
                    N
                  </Box>
                )}
                {isReview && reviewCount > 0 && (
                  <Box
                    className="white"
                    padding="1px 6px"
                    backgroundColor={colors.orange700}
                    borderRadius="20px"
                    css={{
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: "500",
                      lineHeight: "18px",
                    }}
                  >
                    {reviewCount}
                  </Box>
                )}
              </HStack>
            </TabBar.Tab>
          );

          return (
            <Box
              position="relative"
              key={i}
              css={responsive({
                borderBottom: {
                  base: isActive ? `2px solid ${colors.gray900}` : "none",
                  lg: "none",
                },
              })}
            >
              {isExternalLink ? (
                <a href={item.to} css={{ outline: "none", textDecoration: "none" }}>
                  {tabContent}
                </a>
              ) : (
                <NextLink href={to}>{tabContent}</NextLink>
              )}
            </Box>
          );
        })}
      </TabBar>
    </Box>
  );
}

export function HeaderButtons({
  isHomeViewportTop,
  homePath,
}: {
  isHomeViewportTop: boolean;
  homePath: string;
}) {
  // const isLoggedIn = useRecoilValue(isLoggedInState);
  const router = useRouter();

  // 콜 유효율이 낮은 페이지에서는 GNB 전화 CTA를 숨긴다. 롤백은 useHideCallCta 참고.
  const hideCallCta = useHideCallCta();

  /**
   * 화장장 화면에서는 바로 걸지 않고 문의 유형 모달을 띄운다. 모달 안 이벤트는
   * 화면별 path 로 쏘므로 pathname 에서 path 를 같이 뽑는다.
   *
   * pathname 으로 판정하는 이유는 useHideCallCta 와 같다. SSR/CSR 값이 같아 하이드레이션
   * 불일치가 없고, router.query 는 SSG 하이드레이션 전에 비어 있어 렌더 시점에 쓸 수 없다.
   */
  const inquiryEventPath = CREMATORIUM_INQUIRY_EVENT_PATH[router.pathname];
  /** 화장장 화면이 아니면 모달을 열지 않으므로 이때 넘기는 path 는 쓰이지 않는다. */
  const { openInquiryModal } = useCrematoriumInquiryModal(inquiryEventPath ?? "crematorium_detail");

  const parlorInquiryEventPath = PARLOR_INQUIRY_EVENT_PATH[router.pathname];
  /** 장례식장 화면이 아니면 모달을 열지 않으므로 이때 넘기는 path 는 쓰이지 않는다. */
  const { openParlorInquiryModal } = useParlorInquiryModal(
    parlorInquiryEventPath ?? "parlor_detail",
  );

  /**
   * Header 는 전역 컴포넌트라 화장장 데이터를 들고 있지 않다. 화장장 목록 JSON 이
   * 전 페이지 번들에 실리지 않도록 클릭 시점에 동적으로 불러온다. 클릭은 하이드레이션
   * 이후라 이 시점의 router.query.id 는 채워져 있다.
   * 불러오기가 실패해도 모달은 열리고, 이때는 모달 안에서 고이 대표번호로 폴백한다.
   */
  const handleCallCtaClick = async () => {
    if (parlorInquiryEventPath) {
      await openParlorInquiryModal();
      return;
    }

    if (!inquiryEventPath) {
      call("1666-9784");
      return;
    }

    let facilityPhone: string | null = null;

    try {
      const { getCrematoriumPhoneByRouteId } = await import("@/utils/crematoriumPhone");
      facilityPhone = getCrematoriumPhoneByRouteId(router.query.id);
    } catch {
      facilityPhone = null;
    }

    openInquiryModal(facilityPhone);
  };

  return (
    <>
      {/* {isLoggedIn || (
        <div
          className="body3_m"
          css={{
            padding: "6px 6px",
            backgroundColor: isHomeViewportTop ? "transparent" : "white",
            color: isHomeViewportTop ? "white" : "black",
            border: `1px solid ${colors.gray200}`,
            borderRadius: "5px",
            marginRight: "8px",
            tansition: "all 0.1s linear",
          }}
          onClick={() => {
            customEvent({
              newGtm: {
                path: "gnb",
                trigger_and_target: "contract_check-prime",
              },
            });
            router.push(`https://prime.goifuneral.co.kr/?utm_campaign=platform`);
          }}
        >
          100원상조
        </div>
      )} */}
      {
        // isLoggedIn ? (
        //   <HeaderBtn
        //     type="BLACK"
        //     onClick={() => {
        //       router.push("/step-guide/initiation/");
        //     }}
        //     eventTrigger="step-guide"
        //   >
        //     장례준비 가이드
        //   </HeaderBtn>
        // ) :
        // <HeaderBtn
        //   type="ORANGE"
        //   onClick={() => {
        //     router.push("/funeral-prepare/1/");
        //   }}
        //   eventTrigger="srvc_rcmms-uv_funeral_prepare_1"
        // >
        //   장례가 처음이라면?
        // </HeaderBtn>

        hideCallCta || (
          <HeaderButton
            path="gnb"
            type="RED"
            onClick={() => {
              void handleCallCtaClick();
            }}
            eventTrigger="srvc_rcmms-uv_funeral_prepare_1"
          >
            24시간 고객센터
          </HeaderButton>
        )
      }
    </>
  );
}

export const GOI_SERVICE_ALL_PACKAGES = [
  {
    name: "무빈소장",
    urlPath: "무빈소장",
    imageSrc: "platform/service/service_simple_figure.png",
    imageAlt: "무빈소장 품목",
  },
  {
    name: "가족장",
    urlPath: "가족장",
    imageSrc: "platform/service/service_family_figure.png",
    imageAlt: "가족장 품목",
  },
  {
    name: "일반장",
    urlPath: "일반장",
    imageSrc: "platform/service/service_general_figure.png",
    imageAlt: "일반장 품목",
  },
];

function HeaderServiceTabBar({ year }: { year: 2025 | 2026 }) {
  const { currentServiceType } = useServicePackage(year);

  const { isHide, firstOffsetCondition } = useHandleHeaderServiceTabBar({
    firstOffset: SECOND_HEADER_HEIGHT,
    secondOffsetId: "service-navigator-bar",
  });

  const { isBiggerThanLg } = useResponsive();

  // year에 따라 URL 생성 함수
  const getServiceUrl = (urlPath: string) => {
    return year === 2025 ? `/service/${year}/${urlPath}/` : `/service/${urlPath}/`;
  };

  return isHide ? null : (
    <HStack
      width="100%"
      maxWidth="480px"
      gap="10px"
      className={`header`}
      css={{
        position: "fixed",
        top: isBiggerThanLg
          ? HEADER_HEIGHT
          : HEADER_HEIGHT + SECOND_HEADER_HEIGHT - firstOffsetCondition,
        background: "white",
        padding: "0 16px",
        overflowX: "auto",
        zIndex: 999,
        maxWidth: `${GLOBAL_MAX_WIDTH}px`,
        margin: "0 auto",
        left: 0,
        right: 0,
        "&::-webkit-scrollbar": {
          display: "none",
        },
        borderBottom: `1px solid ${colors.gray100}`,
      }}
    >
      {GOI_SERVICE_ALL_PACKAGES.map(({ name, urlPath }) => (
        <Box
          key={name}
          flex="0 0 auto"
          className={name === currentServiceType ? "subtitle_16_b gray900" : "body_16_m gray500"}
          css={{
            padding: "10px 4px",
            borderBottom: name === currentServiceType ? `2px solid ${colors.gray900}` : "none",
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
        >
          <Link
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "service",
                  trigger_and_target: "tab_type-view_type",
                  text: name,
                },
              });
            }}
            href={getServiceUrl(urlPath)}
          >
            {name}
          </Link>
        </Box>
      ))}
    </HStack>
  );
}

function HeaderProductTabBar() {
  const router = useRouter();
  const menus = useMemo(
    () => [
      { name: "전체상품", url: "/product/" },
      { name: "고이프라임", url: getPrimeUrl("/") },
      { name: "자주묻는질문", url: getPrimeUrl("/faq") },
      { name: "회사소개", url: "/funeral-prepare/company-introduction/" },
    ],
    [],
  );
  const currentMenuName = useMemo(() => {
    if (router.pathname === "/product") return menus[0].name;
    else if (router.pathname.includes("company-introduction")) return menus[3].name;
    else return "";
  }, [router, menus]);
  const { isHide, firstOffsetCondition } = useHandleHeaderServiceTabBar({
    firstOffset: SECOND_HEADER_HEIGHT,
    secondOffsetId: "product-navigator-bar",
  });
  const { isBiggerThanLg } = useResponsive();
  return isHide ? null : (
    <HStack
      width="100%"
      maxWidth="480px"
      gap="10px"
      className={`header`}
      css={{
        position: "fixed",
        top: isBiggerThanLg
          ? HEADER_HEIGHT
          : HEADER_HEIGHT + SECOND_HEADER_HEIGHT - firstOffsetCondition,
        background: "white",
        padding: "0 16px",
        overflowX: "auto",
        zIndex: 999,
        maxWidth: `${GLOBAL_MAX_WIDTH}px`,
        margin: "0 auto",
        left: 0,
        right: 0,
        "&::-webkit-scrollbar": {
          display: "none",
        },
        borderBottom: `1px solid ${colors.gray100}`,
      }}
    >
      {menus.map(({ name, url }) => (
        <Box
          key={name}
          flex="0 0 auto"
          className={name === currentMenuName ? "subtitle_16_b gray900" : "body_16_m gray500"}
          css={{
            padding: "10px 4px",
            borderBottom: name === currentMenuName ? `2px solid ${colors.gray900}` : "none",
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
        >
          <Link
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "product_main",
                  trigger_and_target: "tab_type-view_type",
                  text: name,
                },
              });
            }}
            href={url}
          >
            {name}
          </Link>
        </Box>
      ))}
    </HStack>
  );
}
export const useHandleHeaderServiceTabBar = ({
  firstOffset = 46,
  secondOffsetId = "",
}: {
  firstOffset?: number;
  secondOffsetId?: string;
}) => {
  const [isHide, setIsHide] = useState(false);
  const [firstOffsetCondition, setFirstOffsetCondition] = useState(0);
  const lastScrollYRef = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const handleScroll: any = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          const isScrollingDown = currentScrollY > lastScrollYRef.current;
          const isScrollingUp = currentScrollY < lastScrollYRef.current;

          const top = 0;

          // iOS 바운스 스크롤을 고려한 최상단 체크
          if (currentScrollY <= 5) {
            setFirstOffsetCondition(0);
            setIsHide(false);
          } else {
            setFirstOffsetCondition(currentScrollY > 0 ? firstOffset : 0);

            const el = document.getElementById(secondOffsetId);

            if (currentScrollY < top) {
              setFirstOffsetCondition(firstOffset);
              setIsHide(false);
            } else {
              // 스크롤이 조금이라도 움직이면 바로 처리
              if (isScrollingDown) {
                setFirstOffsetCondition(firstOffset);
                if (el && el.getBoundingClientRect().top <= HEADER_HEIGHT + 8) {
                  setIsHide(true);
                } else {
                  setIsHide(firstOffset > 0 ? false : true);
                }
              } else if (isScrollingUp) {
                setFirstOffsetCondition(firstOffset > 0 ? 0 : firstOffset);
                setIsHide(false);
              }
            }
          }

          lastScrollYRef.current = currentScrollY;
          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [firstOffset, secondOffsetId]);

  return { isHide, firstOffsetCondition };
};
